import React, { Fragment, useEffect, useState } from 'react'
import { Button, LoadingSpinner, FlagCard, FlagFormModal, ConfirmationModal, FlagCategoryFormModal } from '../components'
import {
    API_BASE_URL, FlagCardMeta, LoadingSpinnerMeta,
    FlagFormModalMeta, UpdateFlagFormMeta, ConfirmationModalMeta, DeleteFlagMeta,
    FlagCategoryFormModalMeta, UpdateFlagCategoryFormMeta, DeleteFlagCategoryMeta
} from '../data'
import { Flag, FlagMetaProps, FlagCategory } from '../data/types'
import { PlusIcon, ChevronLeftIcon, EditIcon, DeleteIcon } from '../icons'
import { Link } from 'react-router-dom'
import { useResourcePermissions } from '../hooks/useResourcePermissions'

type Props = {
    meta: FlagMetaProps,
}

export const Flags = (props: Props) => {
    const { meta } = props
    const { canEditFlag, canDeleteFlag, canCreateFlag } = useResourcePermissions()
    const [loading, setLoading] = useState(true)
    const [flags, setFlags] = useState<Flag[]>([])
    const [categories, setCategories] = useState<FlagCategory[]>([])
    const [error, setError] = useState<string | null>(null)
    const [isCreateFlagModalOpen, setIsCreateFlagModalOpen] = useState(false)
    const [selectedFlag, setSelectedFlag] = useState<Flag | null>(null)
    const [isEditFlagModalOpen, setIsEditFlagModalOpen] = useState(false)
    const [isDeleteFlagModalOpen, setIsDeleteFlagModalOpen] = useState(false)
    const [isDeletingFlag, setIsDeletingFlag] = useState(false)
    const [filterText, setFilterText] = useState<string | null>(null)
    const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState<FlagCategory | null>(null)
    const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false)
    const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] = useState(false)
    const [isDeletingCategory, setIsDeletingCategory] = useState(false)

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            setError(null)

            try {
                // Fetch flags
                const flagsResponse = await fetch(`${API_BASE_URL}/flag`, { credentials: 'include' })
                if (!flagsResponse.ok) {
                    const text = await flagsResponse.text()
                    const textObj = JSON.parse(text)
                    throw new Error(`${textObj.message}`)
                }
                const flagsResult = await flagsResponse.json()
                let flagsData = flagsResult.value || flagsResult
                if (Array.isArray(flagsData) && flagsData.length > 0 && Array.isArray(flagsData[0])) {
                    flagsData = flagsData[0]
                }
                setFlags(Array.isArray(flagsData) ? flagsData : [])

                // Fetch categories
                const categoriesResponse = await fetch(`${API_BASE_URL}/flag-category`, { credentials: 'include' })
                if (!categoriesResponse.ok) {
                    const text = await categoriesResponse.text()
                    const textObj = JSON.parse(text)
                    throw new Error(`${textObj.message}`)
                }
                const categoriesResult = await categoriesResponse.json()
                let categoriesData = categoriesResult.value || categoriesResult
                if (Array.isArray(categoriesData) && categoriesData.length > 0 && Array.isArray(categoriesData[0])) {
                    categoriesData = categoriesData[0]
                }
                setCategories(Array.isArray(categoriesData) ? categoriesData : [])
            }
            catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching data')
            }
            finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [])

    const handleCreateFlag = async (flagData: any) => {
        const response = await fetch(`${API_BASE_URL}/flag`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(flagData)
            })

        if (!response.ok) {
            const text = await response.text()
            const textObj = JSON.parse(text)
            throw new Error(`${textObj.message}`)
        }

        const result = await response.json()

        if (result.model) {
            setFlags(prevFlags => [...prevFlags, result.model])
        }
    }

    const handleUpdateFlag = async (flagData: { name: string, description: string, category_id: number | null, has_expiry: ConstrainBoolean, hidden_from_registration: boolean }) => {
        if (!selectedFlag) return

        const response = await fetch(`${API_BASE_URL}/flag/${selectedFlag.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                name: flagData.name,
                description: flagData.description,
                category_id: flagData.category_id,
                has_expiry: flagData.has_expiry,
                hidden_from_registration: flagData.hidden_from_registration
            })
        })

        if (!response.ok) {
            const text = await response.text()
            const errorText = JSON.parse(text)
            throw new Error(errorText.message);
        }

        const result = await response.json()

        if (result.model) {
            const updatedFlag = result.model
            setFlags(prev =>
                prev.map(flag =>
                    flag.id === updatedFlag.id ? updatedFlag : flag
                )
            )
        }
    }

    const handleDeleteFlagConfirm = async () => {
        if (!selectedFlag) return

        setIsDeletingFlag(true)
        try {
            const response = await fetch(`${API_BASE_URL}/flag/${selectedFlag.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                const text = await response.text()
                const errorText = JSON.parse(text)
                throw new Error(errorText.message);
            }

            setFlags(prev => prev.filter(flag => flag.id !== selectedFlag.id));
            setIsDeleteFlagModalOpen(false)
            setSelectedFlag(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete Flag')
            setIsDeleteFlagModalOpen(false)
        } finally {
            setIsDeletingFlag(false)
        }
    }

    const handleEditFlagClick = (flag: Flag) => {
        setSelectedFlag(flag)
        setIsEditFlagModalOpen(true)
    }

    const handleDeleteFlagClick = (flag: Flag) => {
        setSelectedFlag(flag)
        setIsDeleteFlagModalOpen(true)
    }

    const handleDeleteFlagCancel = () => {
        setIsDeleteFlagModalOpen(false)
        setSelectedFlag(null)
    }

    const handleCreateCategory = async (categoryData: any) => {
        const response = await fetch(`${API_BASE_URL}/flag-category`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(categoryData)
            })

        if (!response.ok) {
            const text = await response.text()
            const textObj = JSON.parse(text)
            throw new Error(`${textObj.message}`)
        }

        const result = await response.json()

        if (result.model) {
            setCategories(prevCategories => [...prevCategories, result.model])
        }
    }

    const handleUpdateCategory = async (categoryData: { name: string }) => {
        if (!selectedCategory) return

        const response = await fetch(`${API_BASE_URL}/flag-category/${selectedCategory.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                name: categoryData.name
            })
        })

        if (!response.ok) {
            const text = await response.text()
            const errorText = JSON.parse(text)
            throw new Error(errorText.message);
        }

        const result = await response.json()

        if (result.model) {
            const updatedCategory = result.model
            setCategories(prev =>
                prev.map(category =>
                    category.id === updatedCategory.id ? updatedCategory : category
                )
            )
        }
    }

    const handleDeleteCategoryConfirm = async () => {
        if (!selectedCategory) return

        setIsDeletingCategory(true)
        try {
            const response = await fetch(`${API_BASE_URL}/flag-category/${selectedCategory.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                const text = await response.text()
                const errorText = JSON.parse(text)
                throw new Error(errorText.message);
            }

            setCategories(prev => prev.filter(category => category.id !== selectedCategory.id));
            setIsDeleteCategoryModalOpen(false)
            setSelectedCategory(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete Category')
            setIsDeleteCategoryModalOpen(false)
        } finally {
            setIsDeletingCategory(false)
        }
    }

    const handleEditCategoryClick = (category: FlagCategory) => {
        setSelectedCategory(category)
        setIsEditCategoryModalOpen(true)
    }

    const handleDeleteCategoryClick = (category: FlagCategory) => {
        setSelectedCategory(category)
        setIsDeleteCategoryModalOpen(true)
    }

    const handleDeleteCategoryCancel = () => {
        setIsDeleteCategoryModalOpen(false)
        setSelectedCategory(null)
    }

    const filterFlags = flags.filter((flag) => {
        if (filterText === null)
            return true
        const searchText = filterText.toLowerCase()
        return flag.name.toLowerCase().includes(searchText) ||
            (flag.description && flag.description.toLowerCase().includes(searchText))
    })

    // Group flags by category
    const flagsByCategory: { [key: string]: Flag[] } = {}
    const uncategorizedFlags: Flag[] = []

    filterFlags.forEach((flag) => {
        if (flag.category) {
            const categoryName = flag.category.name
            if (!flagsByCategory[categoryName]) {
                flagsByCategory[categoryName] = []
            }
            flagsByCategory[categoryName].push(flag)
        } else {
            uncategorizedFlags.push(flag)
        }
    })

    // Sort category names and flags within categories alphabetically
    const sortedCategoryNames = Object.keys(flagsByCategory).sort()
    Object.keys(flagsByCategory).forEach(categoryName => {
        flagsByCategory[categoryName].sort((a, b) => a.name.localeCompare(b.name))
    })
    uncategorizedFlags.sort((a, b) => a.name.localeCompare(b.name))

    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className='mb-4'>
                        <Link
                            to="/admin"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            Back to Admin Hub
                        </Link>
                    </div>
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                            {meta.description}
                        </p>

                        {/* Categories Section */}
                        <div className="mb-8 max-w-4xl mx-auto">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                        {meta.categorySectionTitle}
                                    </h2>
                                    {canCreateFlag() && (
                                        <Button onClick={() => setIsCreateCategoryModalOpen(true)} size="sm">
                                            <PlusIcon className="w-4 h-4" />
                                            {meta.createCategoryButton}
                                        </Button>
                                    )}
                                </div>
                                {categories.length === 0 ? (
                                    <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                                        <p>{meta.noCategoryMessage}</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {categories.map((category) => (
                                            <div
                                                key={category.id}
                                                className="inline-flex items-center gap-2 px-3 py-2 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full"
                                            >
                                                <span className="font-medium">{category.name}</span>
                                                {canCreateFlag() && (
                                                    <>
                                                        <button
                                                            onClick={() => handleEditCategoryClick(category)}
                                                            className="hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
                                                            title="Edit category"
                                                        >
                                                            <EditIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteCategoryClick(category)}
                                                            className="hover:text-danger-600 dark:hover:text-danger-400 transition-colors"
                                                            title="Delete category"
                                                        >
                                                            <DeleteIcon className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {canCreateFlag() && <div className="flex justify-center">
                            <Button onClick={() => setIsCreateFlagModalOpen(true)} size="lg">
                                <PlusIcon className="w-5 h-5" />
                                {meta.createButton}
                            </Button>
                        </div>}
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {error && (
                        <div className="max-w-2xl mx-auto">
                            <div
                                className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && flags.length === 0 && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            <p className="text-xl">{meta.noFlagMessage}</p>
                        </div>
                    )}

                    {!loading && !error && flags.length > 0 && (
                        <>
                            <div className="mb-8 flex justify-center">
                                <input
                                    type="text"
                                    placeholder={meta.flagSearchPlaceHolder}
                                    value={filterText || ''}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    className="w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 outline-none"
                                />
                            </div>
                            {filterFlags.length > 0 ? (
                                <div className="space-y-8">
                                    {/* Uncategorized flags */}
                                    {uncategorizedFlags.length > 0 && (
                                        <div>
                                            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                                                General
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {uncategorizedFlags.map((flag) => (
                                                    <FlagCard key={flag.id} flag={flag} meta={FlagCardMeta}
                                                        onDelete={canDeleteFlag(flag) ? handleDeleteFlagClick : undefined}
                                                        onEdit={canEditFlag(flag) ? handleEditFlagClick : undefined} />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Categorized flags */}
                                    {sortedCategoryNames.map((categoryName) => (
                                        <div key={categoryName}>
                                            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                                                {categoryName}
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {flagsByCategory[categoryName].map((flag) => (
                                                    <FlagCard key={flag.id} flag={flag} meta={FlagCardMeta}
                                                        onDelete={canDeleteFlag(flag) ? handleDeleteFlagClick : undefined}
                                                        onEdit={canEditFlag(flag) ? handleEditFlagClick : undefined} />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div>
                                    <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                                        <p className="text-xl">{meta.noFlagMessage}</p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                </div>
            </div>

            <FlagFormModal
                meta={FlagFormModalMeta}
                isOpen={isCreateFlagModalOpen}
                onClose={() => {
                    setIsCreateFlagModalOpen(false)
                    setSelectedFlag(null)
                }}
                onSubmit={handleCreateFlag}
                categories={categories}
            />

            <FlagFormModal
                meta={{ ...FlagFormModalMeta, ...UpdateFlagFormMeta }}
                isOpen={isEditFlagModalOpen}
                onClose={() => {
                    setIsEditFlagModalOpen(false)
                    setSelectedFlag(null)
                }}
                onSubmit={handleUpdateFlag}
                initialFlag={selectedFlag}
                categories={categories}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeleteFlagModalOpen}
                onConfirm={handleDeleteFlagConfirm}
                onCancel={handleDeleteFlagCancel}
                title={DeleteFlagMeta.confirmTitle}
                message={DeleteFlagMeta.confirmMessage}
                isLoading={isDeletingFlag}
            />

            <FlagCategoryFormModal
                meta={FlagCategoryFormModalMeta}
                isOpen={isCreateCategoryModalOpen}
                onClose={() => {
                    setIsCreateCategoryModalOpen(false)
                    setSelectedCategory(null)
                }}
                onSubmit={handleCreateCategory}
            />

            <FlagCategoryFormModal
                meta={{ ...FlagCategoryFormModalMeta, ...UpdateFlagCategoryFormMeta }}
                isOpen={isEditCategoryModalOpen}
                onClose={() => {
                    setIsEditCategoryModalOpen(false)
                    setSelectedCategory(null)
                }}
                onSubmit={handleUpdateCategory}
                initialCategory={selectedCategory}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeleteCategoryModalOpen}
                onConfirm={handleDeleteCategoryConfirm}
                onCancel={handleDeleteCategoryCancel}
                title={DeleteFlagCategoryMeta.confirmTitle}
                message={DeleteFlagCategoryMeta.confirmMessage}
                isLoading={isDeletingCategory}
            />
        </Fragment>
    )
}
